import { ThreatLevel } from '@/reports/types/supporting';
import { Stack, styled } from '@/theme/index';
import {
  FraudGauge,
  SectionTitle,
  ShortStack,
  TallStack,
  ThreatItem,
  VerticalDivider,
} from '../components';
import { getThreatLevelTitle, getThreatLevel } from '../utils';

const SecondaryTallStack = styled(TallStack)(({ theme }) => ({
  gap: theme.spacing(1),
  flexDirection: 'column',
  marginTop: theme.spacing(1),
  height: '150px',

  [theme.breakpoints.down('md')]: {
    border: 'none',
  },

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: 0,
  },
}));

const TertiaryTallStack = styled(TallStack)(({ theme }) => ({
  width: '100%',
  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    width: '50%',
    border: 'none',
  },
}));

export interface FraudIpThreatDetectionProps {
  fraudScore: number;
  abuseVelocity: string;
  isVpn?: boolean;
  isRecentAbuse?: boolean;
  isFrequentAbuser?: boolean;
  isHighRiskAttack?: boolean;
  isTor?: boolean;
  isSuspectedProxy?: boolean;
  isBotStatus?: boolean;
}

type ThreatLevelDetail = {
  threatLevel: ThreatLevel;
  text: string;
};

type Mapping = {
  [key: string]: ThreatLevelDetail;
};

function getAbuseVelocityDetails(
  abuseVelocity?: string | null
): ThreatLevelDetail {
  const mapping: Mapping = {
    unknown: { threatLevel: 'unknown', text: 'Unknown Abuse Velocity' },
    high: { threatLevel: 'high', text: 'High Abuse Velocity' },
    medium: { threatLevel: 'medium', text: 'Medium Abuse Velocity' },
    low: { threatLevel: 'low', text: 'Low Abuse Velocity' },
    none: { threatLevel: 'low', text: 'No Abuse' },
  };
  return (
    mapping[abuseVelocity ?? 'unknown'] ?? {
      threatLevel: 'unknown',
      text: 'Unknown Abuse Velocity',
    }
  );
}

export function FraudIpThreatDetection({
  fraudScore,
  isVpn,
  isRecentAbuse,
  isFrequentAbuser,
  isHighRiskAttack,
  abuseVelocity,
  isTor,
  isSuspectedProxy,
  isBotStatus,
}: FraudIpThreatDetectionProps) {
  const abuseVelocityDetails = getAbuseVelocityDetails(abuseVelocity);

  return (
    <Stack>
      <SectionTitle>Threat Detection</SectionTitle>

      <Stack gap={1} direction={{ xs: 'column', md: 'row' }}>
        <Stack gap={1} direction={{ xs: 'row', md: 'column' }}>
          <Stack width={{ xs: '100%', md: '187px' }}>
            <FraudGauge
              title="FraudScan Score"
              fraudScore={fraudScore}
              tooltipText="Indicates the overall fraud score of the user based on multiple variables included in the report. Fraud scores over 75 are suspicious but not necessarily fraudulent or malicious. Fraud scores at or above 90 should be considered high risk."
            />
          </Stack>

          <ShortStack minWidth={{ xs: '33%' }} justifyContent="center">
            <ThreatItem
              text={isVpn ? 'VPN Detected' : 'VPN Not Detected'}
              tooltipText="Indicates whether the IP address is suspected of being a VPN connection."
              width="calc(100% - 16px)"
            />
          </ShortStack>
        </Stack>

        <TallStack
          direction="row"
          alignItems="center"
          width="100%"
          height={{ xs: '138px', md: '183px' }}
        >
          <ThreatItem
            text={getThreatLevelTitle(
              isRecentAbuse,
              'Recent Abuse',
              'No Recent Abuse',
              'Unknown'
            )}
            threatLevel={getThreatLevel(isRecentAbuse)}
            tooltipText="Indicates if there has been recently detected abuse from this IP address. Abuse could include a confirmed chargeback, compromised device, fake app install, or similar malicious behavior within the past few days."
            width="33%"
          />
          <VerticalDivider />
          <ThreatItem
            text={getThreatLevelTitle(
              isFrequentAbuser,
              'Frequent Abuse',
              'No Frequent Abuse',
              'Abuse Frequency Unknown'
            )}
            tooltipText="Indicates if this IP address has a consistent history of abusive behavior across 6 months or more. This data point can be helpful in identifying anonymous IP addresses that are frequently used for malicious behavior, compared to those that may have only been briefly or temporarily compromised."
            threatLevel={getThreatLevel(isFrequentAbuser)}
            width="33%"
          />
          <VerticalDivider />
          <ThreatItem
            text={getThreatLevelTitle(
              isHighRiskAttack,
              'Malicious Activity',
              'No Malicious Activity',
              'Abuse Frequency Unknown'
            )}
            tooltipText="Indicates if this IP address has engaged in malicious abuse such as phishing, brute forcing, DDoS, credential stuffing & account takeover, scraping, form submission spam, and similar attacks."
            threatLevel={getThreatLevel(isHighRiskAttack)}
            width="33%"
          />
        </TallStack>
      </Stack>

      <SecondaryTallStack>
        <TertiaryTallStack direction="row">
          <ThreatItem
            text={abuseVelocityDetails.text}
            tooltipText="Indicates how frequently the IP address is likely to be engaging in abuse. This metric can be used in combination with the FraudScan Score to identify bad behavior."
            threatLevel={abuseVelocityDetails.threatLevel}
            width="100%"
          />
          <VerticalDivider />
          <ThreatItem
            text={getThreatLevelTitle(
              isTor,
              'TOR Activity',
              'No TOR Activity',
              'TOR Activity Unknown'
            )}
            tooltipText="Identifies active TOR exits on the TOR network, which may suggest this IP address is associated with suspicious activity."
            threatLevel={getThreatLevel(isTor)}
            width="100%"
          />
          <VerticalDivider
            sx={{ height: '70%', display: { xs: 'none', md: 'block' } }}
          />
        </TertiaryTallStack>

        <TertiaryTallStack direction="row">
          <ThreatItem
            text={getThreatLevelTitle(
              isSuspectedProxy,
              'Suspected Proxy',
              'No Suspected Proxy',
              'Unknown'
            )}
            tooltipText="Proxies are generally used to attempt to mask the user’s true IP address. An IP address will always flag as a suspected proxy if a VPN connection is detected."
            threatLevel={getThreatLevel(isSuspectedProxy)}
            width="100%"
          />
          <VerticalDivider />
          <ThreatItem
            text={getThreatLevelTitle(
              isBotStatus,
              'Bot Activity',
              'No Bot Activity',
              'Unknown Bot Activity'
            )}
            tooltipText="Indicates if bots or non-human traffic have recently used this IP address to engage in automated fraudulent behavior. This metric provides stronger confidence that the IP address is suspicious."
            threatLevel={getThreatLevel(isBotStatus)}
            width="100%"
          />
        </TertiaryTallStack>
      </SecondaryTallStack>
    </Stack>
  );
}
